/**
 * RequireSubscription - Route wrapper that enforces an active subscription.
 *
 * Sits inside RequireOrg (Layer 2: has org?) to gate feature routes.
 * If subscription is loading, shows a loading spinner.
 * If subscription is null/inactive/missing, redirects to billing page.
 * If subscription is active or trialing, renders children.
 *
 * Settings/billing routes should NOT be wrapped with this component
 * so users can manage their subscription when it's inactive.
 */

import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { billingWithIntent } from '../config/routes';

interface RequireSubscriptionProps {
  children?: ReactNode;
}

export function RequireSubscription({ children }: RequireSubscriptionProps) {
  const { data: subscription, isLoading, error } = useSubscription();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  // Unexpected query error (not 402 — those return null data)
  if (error) {
    return <Navigate to={billingWithIntent('subscribe')} replace />;
  }

  // No subscription (null from 402) or not active/trialing
  const status = subscription?.subscriptionStatus;
  if (!subscription || (status !== 'active' && status !== 'trialing')) {
    return <Navigate to={billingWithIntent('subscribe')} replace />;
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}
